import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { ButtonGroup, Spacing, Text } from '@vkontakte/vkui';
import config from 'Cloud/config';
import classNames from 'clsx';
import { ReactComponent as FolderIcon } from 'img/folderIcon.svg';
import { extInfo } from 'lib/extInfo';
import { getFaviconByExtension } from 'lib/getFaviconByExtension';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { overquotaNewUnsub } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getHomeFolderItems } from 'reactApp/modules/home/home.selectors';
import type { HomeFile, HomeItem } from 'reactApp/modules/home/home.types';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { OverquotaSubscriptionWidget } from 'reactApp/ui/OverquotaFilesModal/OverquotaSubscriptionWidget';
import { sendDwh } from 'reactApp/utils/ga';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';

import styles from './OverquotaFilesModal.css';

interface Props {
    subscription: BillingDataSubscriptionInfo;
    onClose?: () => void;
    onCancel?: () => void;
    onSuccess?: () => void;
    onAction?: () => void;
}

const sendRadar = (action: string, count_files: number) => {
    sendDwh({
        label: `my-tariff-cancel-ovr-fls-${action}`,
        eventCategory: 'payment',
        p: 'cloud-web-analytics',
        dwh_enabled: true,
        dwhData: { count_files },
    });
};

const messages = {
    description: 'Эти файлы могут не поместиться',
    buttonPrimary: 'Сохранить подписку',
    buttonSecondary: 'Рискну важными файлами',
};

const buildUrls = config.get('BUILD_URLS');
const imgPath = buildUrls.img;

export const OverquotaFilesModal = (props: Props) => {
    const homeItems = useSelector(getHomeFolderItems);
    const isMobile = EnvironmentSelectors.isMobile();
    const ref = useRef<HTMLDivElement>(null);
    const maxFileNameLength = isMobile ? 20 : 28;

    const availableSpace = useSelector(UserQuotaSelectors.getTotalOrBase);
    const spaceLeft = availableSpace - props.subscription.space.original;

    const itemsToShow = homeItems
        .filter((item): item is HomeItem => !!item)
        .sort((a, b) => b.size - a.size)
        .slice(0, overquotaNewUnsub.foldersCount)
        .map((item) => {
            if (item.isFolder) {
                return {
                    type: 'folder',
                    name: item.name,
                    size: item.size,
                    icon: <FolderIcon className={styles.icon} />,
                };
            }
            const extension = extInfo.get((item as HomeFile).ext);
            const icon = getFaviconByExtension(extension, imgPath).svg;
            return {
                type: 'file',
                name: item.name,
                size: item.size,
                icon: <img src={icon ? icon : undefined} className={styles.icon} />,
            };
        });

    const count = itemsToShow.length;

    useEffect(() => {
        sendRadar('show', count);
    }, []);

    const onClick = useCallback(() => {
        props.onClose?.();
        sendRadar('save', count);
    }, []);

    const onSecondaryClick = useCallback(() => {
        props.onCancel?.();
        sendRadar('cancel', count);
    }, []);

    const onClose = useCallback(() => {
        props.onClose?.();
        sendRadar('close', count);
    }, []);

    const getTitle = useCallback(() => {
        return `Без подписки останется только ${bytesToNDigits(spaceLeft, 3).value}`;
    }, []);

    const buttons = useMemo(() => {
        return (
            <ButtonGroup mode={isMobile ? 'vertical' : 'horizontal'} gap={'s'} stretched={isMobile}>
                <Button
                    qaId={'modal-primary-btn'}
                    theme={'vk'}
                    primary
                    onClick={onClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {messages.buttonPrimary}
                </Button>
                <Button
                    qaId={'modal-secondary-btn'}
                    theme={'vk'}
                    secondary
                    onClick={onSecondaryClick}
                    sizeMode={ButtonSizeMode.small}
                    className={classNames(styles.button, { [styles.button_mobile]: isMobile })}
                >
                    {messages.buttonSecondary}
                </Button>
            </ButtonGroup>
        );
    }, []);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={32} />
                <Text className={isMobile ? styles.title_mobile : styles.title}>{getTitle()}</Text>
                <Spacing size={24} />
                <OverquotaSubscriptionWidget isMobile={isMobile} availableSpace={availableSpace} spaceLeft={spaceLeft} />
                {count > 0 && (
                    <>
                        <Spacing size={24} />
                        <Text className={styles.subheader}>{messages.description}</Text>
                        <Spacing size={16} />
                        <div className={styles.items}>
                            {itemsToShow.map((item, index) => (
                                <div key={`file-${index}`} className={styles.itemRow}>
                                    <div className={styles.itemName}>
                                        {item.icon}
                                        <span className={styles.textEllipsis}>{truncateTextMiddle(item.name, maxFileNameLength)}</span>
                                    </div>
                                    <span className={styles.itemSize}>{bytesToNDigits(item.size, 3).value}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <Spacing size={36} />
                {!isMobile && (
                    <>
                        {buttons}
                        <Spacing size={32} />
                    </>
                )}
            </>
        );
    }, []);

    if (isMobile) {
        return (
            <MobileDialog
                id={'outflow-overquota-warning-modal'}
                open
                onClose={onClose}
                closable
                mod={'confirm'}
                topmost
                scrollRef={ref}
                isRebrandingQuotaLanding
                dimmer
                closeOnDimmerClick
                footer={buttons}
                className={styles.card}
            >
                <div ref={ref} className={styles.card}>
                    {content}
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            id={'outflow-overquota-warning-modal'}
            onCancel={onClose}
            closable
            closeOnEscape={true}
            closeOnDimmerClick={false}
            className={styles.card}
            size={'tiny'}
            dimmer={false}
        >
            <div ref={ref} className={styles.card}>
                {content}
            </div>
        </Dialog>
    );
};

export const openOverquotaFilesModal = (data?: Props) =>
    openPopupHelper({ popupName: popupNames.OVERQUOTA_FILES_MODAL, data, onClose: data?.onClose });

export const closeOverquotaFilesModal = () => closePopupHelper(popupNames.OVERQUOTA_FILES_MODAL);
